/**
 * Hiệu ứng âm thanh cho game "Hái Cà Rốt" — tổng hợp bằng Web Audio API.
 * Miễn phí, không cần file nhạc ngoài (tránh CSP/tải mạng), tôn trọng autoplay:
 * AudioContext chỉ tạo sau thao tác đầu tiên của người dùng.
 */
export type CarrotSfx = "click" | "correct" | "wrong" | "harvest" | "finish";

type Ctx = AudioContext;

let ctx: Ctx | null = null;
let muted = false;

function ensureCtx(): Ctx | null {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

/** Phát 1 nốt đơn giản với bao hình lên/xuống mượt. */
function note(
  ac: Ctx,
  freq: number,
  startAt: number,
  dur: number,
  type: OscillatorType,
  peak: number
) {
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, startAt);
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(peak, startAt + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + dur);
  osc.connect(gain).connect(ac.destination);
  osc.start(startAt);
  osc.stop(startAt + dur + 0.02);
}

/** Nốt quét tần số (dùng cho tiếng "pop" thu hoạch). */
function sweep(ac: Ctx, from: number, to: number, startAt: number, dur: number, peak: number) {
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(from, startAt);
  osc.frequency.exponentialRampToValueAtTime(to, startAt + dur);
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(peak, startAt + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + dur);
  osc.connect(gain).connect(ac.destination);
  osc.start(startAt);
  osc.stop(startAt + dur + 0.02);
}

export const carrotSound = {
  get muted() {
    return muted;
  },
  setMuted(v: boolean) {
    muted = v;
  },
  toggle(): boolean {
    muted = !muted;
    return muted;
  },
  /** Gọi trong sự kiện click đầu tiên để "mở khóa" âm thanh trên trình duyệt. */
  unlock() {
    ensureCtx();
  },
  play(name: CarrotSfx) {
    if (muted) return;
    const ac = ensureCtx();
    if (!ac) return;
    const t = ac.currentTime;
    switch (name) {
      case "click":
        note(ac, 320, t, 0.09, "triangle", 0.18);
        break;
      case "correct": // hợp âm rải vui tai (Đô–Mi–Son)
        note(ac, 523.25, t, 0.18, "sine", 0.22);
        note(ac, 659.25, t + 0.08, 0.18, "sine", 0.22);
        note(ac, 783.99, t + 0.16, 0.26, "sine", 0.24);
        break;
      case "wrong": // hai nốt đi xuống, hơi "buzz"
        note(ac, 300, t, 0.18, "sawtooth", 0.16);
        note(ac, 200, t + 0.14, 0.28, "sawtooth", 0.16);
        break;
      case "harvest": // "pop" nhổ cà rốt
        sweep(ac, 420, 900, t, 0.16, 0.2);
        break;
      case "finish": // kèn chúc mừng
        note(ac, 523.25, t, 0.16, "square", 0.16);
        note(ac, 659.25, t + 0.12, 0.16, "square", 0.16);
        note(ac, 783.99, t + 0.24, 0.16, "square", 0.16);
        note(ac, 1046.5, t + 0.36, 0.4, "square", 0.2);
        break;
    }
  },
};
